import { FC, MouseEvent, ReactNode, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AudioPlayer, Icon, InfoTooltipModal, Modal } from 'src/components';
import { useShowSentenceDifficulty } from 'src/hooks';
import { SampleSentence } from 'src/api/learningSessions/types';
import {
  ExampleSentenceSortField,
  getExampleSentenceSort,
  setExampleSentenceSort,
} from 'src/utils/exampleSentenceSortPreference';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import {
  SENTENCE_DIFFICULTY_MAX,
  filterOutFlaggedSentences,
  findLearnableHighlightRange,
  formatSampleSentenceCreator,
  formatSentenceDifficultyScore,
  getSentenceDifficultyTone,
  orderSampleSentencesByRelevance,
  orderSampleSentencesBySort,
  toTargetSentenceDifficulty,
} from './utils';
import * as S from './styles';

const VISIBLE_EXAMPLES_COUNT = 2;

const msgs = defineMessages({
  title: {
    id: 'learning_session_customise_sample_sentences_label',
    defaultMessage: 'Example Sentences',
  },
  audio: {
    id: 'learning_session_learnables_card_audio',
    defaultMessage: 'Audio',
  },
  creatorName: {
    id: 'wordlists_viewscreen_author_name',
    defaultMessage: 'Created by {creator}',
  },
  thirdPartySourcesInfo: {
    id: 'learning_session_example_sentences_third_party_info',
    defaultMessage:
      'Some example sentences on Memrise come from third-party sources. We screen these example sentences for inappropriate content but if you spot something that is offensive or incorrect please click on the flag icon at the top of the screen to let us know.',
  },
  showDifficulty: {
    id: 'learning_session_example_sentences_show_difficulty',
    defaultMessage: 'Show sentence difficulty',
  },
  hideDifficulty: {
    id: 'learning_session_example_sentences_hide_difficulty',
    defaultMessage: 'Hide sentence difficulty',
  },
  sortGroup: {
    id: 'learning_session_example_sentences_sort_group',
    defaultMessage: 'Sort example sentences',
  },
  sortDifficulty: {
    id: 'learning_session_example_sentences_sort_difficulty',
    defaultMessage: 'Sort by difficulty',
  },
  sortDifficultyAsc: {
    id: 'learning_session_example_sentences_sort_difficulty_asc',
    defaultMessage: 'Sort by difficulty, low to high',
  },
  sortDifficultyDesc: {
    id: 'learning_session_example_sentences_sort_difficulty_desc',
    defaultMessage: 'Sort by difficulty, high to low',
  },
  sortLength: {
    id: 'learning_session_example_sentences_sort_length',
    defaultMessage: 'Sort by length',
  },
  sortLengthAsc: {
    id: 'learning_session_example_sentences_sort_length_asc',
    defaultMessage: 'Sort by length, short to long',
  },
  sortLengthDesc: {
    id: 'learning_session_example_sentences_sort_length_desc',
    defaultMessage: 'Sort by length, long to short',
  },
  sortSource: {
    id: 'learning_session_example_sentences_sort_source',
    defaultMessage: 'Sort by source',
  },
  sortSourceAsc: {
    id: 'learning_session_example_sentences_sort_source_asc',
    defaultMessage: 'Sort by source, A to Z',
  },
  sortSourceDesc: {
    id: 'learning_session_example_sentences_sort_source_desc',
    defaultMessage: 'Sort by source, Z to A',
  },
});

const SORT_OPTIONS: {
  field: ExampleSentenceSortField;
  icon: 'lightning' | 'ruler' | 'book';
  labels: {
    idle: keyof typeof msgs;
    asc: keyof typeof msgs;
    desc: keyof typeof msgs;
  };
}[] = [
  {
    field: 'difficulty',
    icon: 'lightning',
    labels: { idle: 'sortDifficulty', asc: 'sortDifficultyAsc', desc: 'sortDifficultyDesc' },
  },
  {
    field: 'length',
    icon: 'ruler',
    labels: { idle: 'sortLength', asc: 'sortLengthAsc', desc: 'sortLengthDesc' },
  },
  {
    field: 'source',
    icon: 'book',
    labels: { idle: 'sortSource', asc: 'sortSourceAsc', desc: 'sortSourceDesc' },
  },
];

export interface ExampleSentencesProps {
  sampleSentences: SampleSentence[];
  learnableValue?: string | null;
  showRomanization?: boolean;
  userScore?: number | null;
  isEnglishSource?: boolean;
  onAudioClick?: () => void;
  onLightningboltClick?: () => void;
  /** Lighten title/actions for use on a dark video overlay panel. */
  overlay?: boolean;
}

const renderHighlightedTarget = (
  targetValue: string,
  learnableValue?: string | null,
): ReactNode => {
  const range = findLearnableHighlightRange(targetValue, learnableValue);
  if (!range) {
    return targetValue;
  }

  return (
    <>
      {targetValue.slice(0, range.start)}
      <S.LearnableHighlight data-testid="example-sentence-learnable-highlight">
        {targetValue.slice(range.start, range.end)}
      </S.LearnableHighlight>
      {targetValue.slice(range.end)}
    </>
  );
};

export const ExampleSentences: FC<ExampleSentencesProps> = ({
  sampleSentences,
  learnableValue,
  showRomanization = true,
  userScore,
  isEnglishSource = false,
  onAudioClick,
  onLightningboltClick,
  overlay = false,
}) => {
  const intl = useIntl();
  const [isAllExamplesOpen, setIsAllExamplesOpen] = useState(false);
  const [sentenceSort, setSentenceSort] = useState(getExampleSentenceSort);
  const { showSentenceDifficulty, setShowSentenceDifficulty } = useShowSentenceDifficulty();

  const handleToggleDifficulty = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setShowSentenceDifficulty(!showSentenceDifficulty);
  };

  const handleSelectSort = (field: ExampleSentenceSortField) => {
    const next = {
      field,
      direction: sentenceSort.field === field && sentenceSort.direction === 'asc' ? 'desc' : 'asc',
    } as const;
    setSentenceSort(next);
    setExampleSentenceSort(next);
    if (field === 'difficulty') {
      setShowSentenceDifficulty(true);
    }
  };

  const orderedSentences = useMemo(
    () =>
      orderSampleSentencesByRelevance(
        filterOutFlaggedSentences(sampleSentences),
        toTargetSentenceDifficulty(userScore),
      ),
    [sampleSentences, userScore],
  );

  const remainingSentences = useMemo(
    () => orderedSentences.slice(VISIBLE_EXAMPLES_COUNT),
    [orderedSentences],
  );

  const overflowSentences = useMemo(
    () =>
      orderSampleSentencesBySort(remainingSentences, sentenceSort.field, sentenceSort.direction),
    [remainingSentences, sentenceSort],
  );

  if (orderedSentences.length === 0) {
    return null;
  }

  const visibleSentences = orderedSentences.slice(0, VISIBLE_EXAMPLES_COUNT);

  const renderSentenceCard = (sentence: SampleSentence) => {
    const creator = formatSampleSentenceCreator(sentence.source, sentence.author);
    const attribution = creator ? intl.formatMessage(msgs.creatorName, { creator }) : null;

    return (
      <S.SentenceCard key={sentence.targetSampleSentenceId} data-testid="example-sentence-card">
        <S.CardContent>
          <S.TargetRow>
            <S.TargetValue dir="auto">
              {renderHighlightedTarget(sentence.targetValue, learnableValue)}
            </S.TargetValue>
            {showSentenceDifficulty && (
              <S.DifficultyPill
                data-testid="example-sentence-difficulty"
                $tone={
                  sentence.difficultyScore !== null
                    ? getSentenceDifficultyTone(sentence.difficultyScore)
                    : undefined
                }
              >
                {sentence.difficultyScore !== null
                  ? formatSentenceDifficultyScore(sentence.difficultyScore)
                  : `–/${SENTENCE_DIFFICULTY_MAX}`}
              </S.DifficultyPill>
            )}
          </S.TargetRow>
          {showRomanization && sentence.romanizationValue && (
            <S.RomanizationValue dir="auto" {...romanizationTextAttrs(sentence.romanizationValue)}>
              {sentence.romanizationValue}
            </S.RomanizationValue>
          )}
          <S.SourceValue dir="auto">{sentence.sourceValue}</S.SourceValue>
        </S.CardContent>
        {(attribution || sentence.audio) && (
          <S.HeaderActions>
            {attribution && (
              <S.AttributionIcon data-testid="example-sentence-attribution">
                <InfoTooltipModal tooltip={attribution} />
              </S.AttributionIcon>
            )}
            {sentence.audio && (
              <S.AudioContainer onClick={onAudioClick}>
                <AudioPlayer
                  ariaText={intl.formatMessage(msgs.audio)}
                  src={sentence.audio}
                  autoPlay={false}
                />
              </S.AudioContainer>
            )}
          </S.HeaderActions>
        )}
      </S.SentenceCard>
    );
  };

  const renderDifficultyToggle = (testId: string, onClick?: () => void) => (
    <S.DifficultyToggleButton
      type="button"
      onClick={event => {
        handleToggleDifficulty(event);
        onClick?.();
      }}
      aria-pressed={showSentenceDifficulty}
      aria-label={intl.formatMessage(
        showSentenceDifficulty ? msgs.hideDifficulty : msgs.showDifficulty,
      )}
      data-testid={testId}
    >
      <Icon name={showSentenceDifficulty ? 'lightning' : 'difficultWordsOutline'} />
    </S.DifficultyToggleButton>
  );

  return (
    <S.GalleryWrapper data-testid="example-sentences" $overlay={overlay}>
      <S.TitleRow>
        <S.GalleryTitle data-testid="example-sentences-title">
          {intl.formatMessage(msgs.title)}
        </S.GalleryTitle>
        {isEnglishSource && (
          <S.TitleInfoIcon data-testid="example-sentences-third-party-info">
            <InfoTooltipModal tooltip={intl.formatMessage(msgs.thirdPartySourcesInfo)} />
          </S.TitleInfoIcon>
        )}
        {remainingSentences.length > 0 && (
          <S.TitleIconButton
            type="button"
            onClick={() => setIsAllExamplesOpen(true)}
            data-testid="example-sentences-view-all"
          >
            <Icon name="eye" />
          </S.TitleIconButton>
        )}
        {renderDifficultyToggle('example-sentences-toggle-difficulty', onLightningboltClick)}
      </S.TitleRow>
      <S.ScrollContainer>{visibleSentences.map(renderSentenceCard)}</S.ScrollContainer>

      {isAllExamplesOpen && (
        <Modal
          isOpen
          handleClose={() => setIsAllExamplesOpen(false)}
          shouldCloseOnOutsideClick
          shouldCloseOnEsc
          darkCloseIcon
        >
          <S.AllExamplesList data-testid="example-sentences-all-modal">
            <S.ModalTitleRow>
              <S.GalleryTitle>{intl.formatMessage(msgs.title)}</S.GalleryTitle>
              <S.SortToggle
                role="group"
                aria-label={intl.formatMessage(msgs.sortGroup)}
                data-testid="example-sentences-sort-toggle"
              >
                {SORT_OPTIONS.map(option => {
                  const isSelected = sentenceSort.field === option.field;
                  const labelKey = isSelected
                    ? option.labels[sentenceSort.direction]
                    : option.labels.idle;

                  return (
                    <S.SortOption
                      key={option.field}
                      type="button"
                      $isSelected={isSelected}
                      aria-pressed={isSelected}
                      aria-label={intl.formatMessage(msgs[labelKey])}
                      data-testid={`example-sentences-sort-${option.field}`}
                      onClick={() => handleSelectSort(option.field)}
                    >
                      <Icon name={option.icon} />
                      {isSelected && (
                        <S.SortDirectionIcon>
                          <Icon name={sentenceSort.direction === 'asc' ? 'up' : 'down'} />
                        </S.SortDirectionIcon>
                      )}
                    </S.SortOption>
                  );
                })}
              </S.SortToggle>
              {renderDifficultyToggle('example-sentences-toggle-difficulty-modal')}
            </S.ModalTitleRow>
            {overflowSentences.map(renderSentenceCard)}
          </S.AllExamplesList>
        </Modal>
      )}
    </S.GalleryWrapper>
  );
};
